/* Southern Lumen - slide-out cart drawer. Built 10 Aug 2026.
 *
 * Owns the ENTIRE #SlBuyForm submit. Separate handlers used to fight over it
 * (shipping protection in southern.js, a Klaviyo Added-to-Cart hook, and the
 * plain POST); this file does the jobs in one place, in order:
 *   validate -> build line items -> add -> open the drawer.
 * No tracking here: Klaviyo and Google get Added to Cart from their own app
 * pixels (28 Sep 2026). Sending it from the theme too counts every add twice.
 * Do not add another submit listener to that form.
 *
 * No-JS is unaffected: without this file the form posts to /cart/add and lands
 * on the cart page, which is exactly what it did before.
 *
 * 9 Oct 2026: shipping protection is offered in the drawer (the "shipping
 * protection" block below). It is settled on the cart AFTER the add, when the
 * drawer opens. The form's own request is unchanged and this is still the only
 * listener on it. The drawer never reloads a page, with one exception: on /cart
 * itself, closing it after a cart change reloads (see ON_CART_PAGE).
 * Tiers, prices, the two switches and the wording are set in the admin panel
 * (the product's variants and its custom.protection metafield). This file reads
 * them off the row the Liquid prints and holds none of them.
 *
 * 15 Oct 2026: when Shopify refuses or cuts an add (most often: every one left is already in the cart), the form is
 * no longer posted again. The drawer opens on the real cart and says why ("a refused or cut add" below). Only an add
 * that got no answer at all still falls back to the plain form post.
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-sl-cart-drawer]');
  if (!root) return;

  var CFG = window.SL_CART_CFG || {};
  var THRESH = CFG.threshold || 15000;

  var $ = function (s, c) { return (c || root).querySelector(s); };
  var panel = $('.sl-cd__panel');
  var itemsEl = $('[data-cd-items]');
  var emptyEl = $('[data-cd-empty]');
  var subEl = $('[data-cd-sub]');
  var subWrap = $('[data-cd-subwrap]');
  var shipMsg = $('[data-cd-shipmsg]');
  var barFill = $('[data-cd-barfill]');
  var recsWrap = $('[data-cd-recs]');
  var recList = $('[data-cd-reclist]');
  var busyEl = $('[data-cd-busy]');
  var nostackEl = $('[data-cd-nostack]');

  /* Marked-down handles, from Liquid. /cart.js returns no tags, so handle is the
     only thing the drawer can match a reduced line on. Empty list = nothing on
     sale = the badge and the note never render. */
  var SALE = {};
  (CFG.saleHandles || []).forEach(function (h) { SALE[String(h).toLowerCase()] = true; });
  function isSale(li) {
    return !!SALE[String(li && li.handle || '').toLowerCase()];
  }

  var pool = [];
  try {
    pool = JSON.parse(document.querySelector('[data-cd-pool]').textContent) || [];
  } catch (e) { pool = []; }

  /* Shopify money. money_format can carry markup, so strip tags and only
     substitute the amount token - a naive replace leaves "${{amount}}" visible. */
  function money(cents) {
    var n = (cents || 0) / 100;
    var s = n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    var f = String(CFG.moneyFormat || '${{amount}}').replace(/<[^>]+>/g, '');
    if (f.indexOf('{{amount}}') > -1) return f.replace('{{amount}}', s);
    if (f.indexOf('{{ amount }}') > -1) return f.replace('{{ amount }}', s);
    return '$' + s;
  }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* 9 Oct 2026: the drawer is on every page, /cart included (the header cart icon opens it there). /cart is
     printed by Liquid from the cart as it was at page load: its own protection row, its line numbers and its
     positional updates[] boxes. A change made in this drawer leaves all of that stale, and a stale page acts on
     the wrong line (a product removed in place of protection, protection added twice, quantities posted against
     the wrong lines by Checkout). So on /cart, and only there, closing the drawer after it changed the cart
     reloads the page, which is the cart page's own rule after every change. Everywhere else nothing reloads.
     If a change is still on the wire when the drawer closes, the reload waits for it (busy(false)). */
  var ON_CART_PAGE = !!(document.body && document.body.classList && document.body.classList.contains('template-cart'));
  var cartWrote = false;   // a cart write has gone out from this file on this page view
  var leaving = false;     // closed on /cart with a change still on the wire: reload when it lands

  function busy(on) {
    busyEl.hidden = !on;
    if (!on && leaving) { leaving = false; window.location.reload(); }
  }

  function api(url, body) {
    cartWrote = true;
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) {
      return r.json().then(function (d) {
        if (!r.ok) throw refused(r.status, d);
        return d;
      }, function (x) {
        if (!r.ok) throw refused(r.status, null);   // a refusal with no JSON body still carries its status
        throw x;
      });
    });
  }
  // 15 Oct 2026: a refusal keeps Shopify's status and its own words, so a caller can tell "Shopify said no" (say why,
  // never send it again) from "no answer at all" (network, no status).
  function refused(status, d) {
    var er = new Error((d && d.description) || 'cart error');
    er.status = status;
    er.shopMsg = String((d && (d.description || d.message)) || '');
    return er;
  }

  function getCart() {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); });
  }

  /* ---------- shipping protection ----------
     9 Oct 2026, Josh: bring shipping protection back, it offsets free US shipping. It only ever lived on /cart, and
     nearly everyone checks out from this drawer, so nobody was seeing it.
     The row is snippets/sl-shipping-protection.liquid (where: 'drawer'), printed hidden as the first thing in the
     drawer's pinned footer, right above the subtotal, so the charge and the total are on screen together. This
     block owns everything it does, and nothing in this block reloads the page:
       - Everything that can be set is printed on the row by the Liquid and read here, nothing else: data-tiers
         (every buyable tier as [variant id, top edge in cents or null, price in cents]), data-offer and
         data-default-on. The tiers are the product's variants and the two switches are its custom.protection
         metafield (the admin panel's Shipping Protection page writes both). No tier edge, no price, no variant id
         and no switch is written in this file.
       - What it covers is the lines that SHIP (shipsOf): a gift card is sent by email, so it is never offered
         protection, never gets it added and never moves the tier. The tier comes from the total of those lines,
         EXCLUDING protection (protWant: the smallest edge that covers the total, else the tier with no top edge;
         the same rule as the Liquid on /cart). A cart no tier covers is not offered protection: no row.
       - The charge is in the cart only while the row is shown for it.
       - protSettle(cart) looks at every cart the drawer is about to draw and makes at most ONE correction:
           nothing left that ships, or no tier covers what is left
                                               -> protection comes out (a cart never holds protection alone)
           on, but the wrong tier or doubled   -> out, then the one right variant back in, quantity 1
                                                  (also a variant that is no longer a tier: the tiers were changed
                                                  in the panel while this cart was open)
           off, default-on is set, and this cart has not declined it -> added
         A correction that fails is left alone until the shopper does something (opens the drawer, changes a
         quantity, adds from the rail, upgrades a pack, taps the switch): at most one attempt per action, never
         from a redraw alone, so nothing here can loop. A tier swap that did not take says so on the row.
       - A decline is the cart attribute sl_prot_declined = '1', the same one /cart uses. It is written BEFORE the
         line comes out and cleared BEFORE the line goes back in, so it sticks when more products are added, and
         it dies with the cart, not with the device.
       - The line itself is never drawn as a product row, never counted in the badge, and never seen by the rail
         or the pack upgrade: the row with its switch stands in for it.
     No row on the page (a dealer account, or the product is not buyable) leaves PROT null: every function here does
     nothing, and a protection line already in the cart shows as an ordinary line, as it always did, so it is never
     hidden with no way to take it out.
     "offer": false in the panel prints a stub with no switch in it (data-offer="false"). PROT stays null, so there
     is no row and nothing is ever added, and PROT_GONE makes protSettle take a protection line left in a cart from
     before back out (the charge goes too). If that removal fails the line shows as an ordinary removable line.
     The buy form's request is untouched: protection is settled on the cart after the add, when the drawer opens. */
  var protEl = $('[data-cd-prot]');
  var PROT = null;
  var PROT_GONE = false;
  try {
    if (protEl && !window.SL_DEALER) {
      if (protEl.getAttribute('data-offer') === 'false') PROT_GONE = true;
      else {
        // [variant id, top edge in cents or null for "no top edge", price in cents]; anything else is not a tier
        var protTs = (JSON.parse(protEl.getAttribute('data-tiers') || '[]') || []).filter(function (t) {
          return t && t[0] > 0 && (t[1] === null || (typeof t[1] === 'number' && t[1] > 0)) && typeof t[2] === 'number';
        });
        if (protTs.length) {
          PROT = { h: protEl.getAttribute('data-handle') || 'shipping-protection',
                   auto: protEl.getAttribute('data-default-on') === 'true', ts: protTs };
        }
      }
    }
  } catch (e) { PROT = null; }
  var protToggle = PROT ? protEl.querySelector('[data-cd-prot-toggle]') : null;
  var protPrice = PROT ? protEl.querySelector('[data-cd-prot-price]') : null;
  var protMsg = PROT ? protEl.querySelector('[data-cd-prot-msg]') : null;
  if (!protToggle) PROT = null;               // no switch, no feature
  var PROT_HANDLE = (protEl && protEl.getAttribute('data-handle')) || 'shipping-protection';
  var PROT_ERR = 'Could not update your cart just now. Please try again.';   // the line /cart shows, word for word
  var protFlash = '';   // one message for the row's status line: the next draw shows it, then it is gone
  var protTried = {};   // corrections that did not take since the shopper last did something: a redraw never repeats them
  var protRun = null;   // the correction in flight; a second pass waits for it and never doubles it
  // Called at the start of everything the shopper does in the drawer: a correction that failed gets one more try.
  function protFresh() { protTried = {}; }

  // Protection is never a recommendation, whatever the pool holds (collections.all includes the product).
  pool = pool.filter(function (p) {
    return p && p.h !== PROT_HANDLE && String(p.ty || '').toLowerCase() !== 'shipping protection';
  });

  function isProt(li) { return !!li && li.handle === PROT_HANDLE; }
  function protOf(cart) { return ((cart && cart.items) || []).filter(isProt); }
  function goodsOf(cart) { return ((cart && cart.items) || []).filter(function (li) { return !isProt(li); }); }
  // The lines protection is for: products that ship. /cart.js marks a gift card (sent by email) gift_card: true and
  // requires_shipping: false. They stay ordinary products everywhere else (rows, badge, rail).
  function shipsOf(cart) {
    return goodsOf(cart).filter(function (li) { return li.requires_shipping !== false && !li.gift_card; });
  }
  function linesTotal(lines) { return lines.reduce(function (s, li) { return s + (li.final_line_price || 0); }, 0); }
  // 9 Oct 2026 (review 2): does the row stand in for the protection line in THIS cart? Only then is the line left
  // out of the rows, the badge and the markdown note. The row is shown while something ships and a tier covers
  // it (protDraw). Where it cannot be shown, protSettle takes the line out; if that call fails the line is still
  // in the cart and in the subtotal, so it is drawn as an ordinary line with its own remove button, the same as
  // after "offer": false. A cart that holds protection and nothing else is the one exception: the drawer shows
  // its empty state there, with no subtotal and no Checkout on screen. protWant is declared further down.
  function protStands(cart) {
    if (!PROT) return false;
    if (!goodsOf(cart).length) return true;
    return linesTotal(shipsOf(cart)) > 0 && !!protWant(cart);
  }
  // "Items" the way the shopper counts them: products. With no row for the line, Shopify's own count, as before.
  function countOf(cart) {
    var n = (cart && cart.item_count) || 0;
    if (!protStands(cart)) return n;
    protOf(cart).forEach(function (li) { n -= li.quantity || 0; });
    return n > 0 ? n : 0;
  }
  function protDeclined(cart) { return !!(cart && cart.attributes && cart.attributes.sl_prot_declined === '1'); }
  // The tier for this cart's goods, as [variant id, top edge, price in cents]: the smallest edge that is >= the
  // total of the lines that ship, else the first tier with no top edge. null when no tier covers the cart (it is
  // over every edge and there is no last tier): that cart is not offered protection. The same rule as
  // snippets/sl-shipping-protection.liquid and as the admin panel; the edges and prices themselves are the row's
  // (data-tiers), never this file's.
  function protWant(cart) {
    var total = linesTotal(shipsOf(cart)), fit = null, open = null;
    PROT.ts.forEach(function (t) {
      if (t[1] === null) { if (!open) open = t; return; }
      if (t[1] >= total && (!fit || t[1] < fit[1])) fit = t;
    });
    return fit || open;
  }
  // The one correction this cart needs, or null. sig names it, so a correction that failed is not repeated.
  function protNeed(cart) {
    if (!cart || !cart.items) return null;
    // "offer": false in the panel: a protection line left in the cart from before comes out, and that is all
    if (PROT_GONE) { var left = protOf(cart); return left.length ? { kind: 'gone', sig: 'gone', have: left } : null; }
    if (!PROT) return null;
    // Something to protect = the lines that ship add up to more than 0, the test the /cart row uses in Liquid.
    var have = protOf(cart), covered = linesTotal(shipsOf(cart)) > 0;
    // ...and a tier covers it. With none the row is not shown, so the charge must not be in the cart either.
    var want = protWant(cart);
    if (!want) covered = false;
    if (!have.length) {
      if (!covered || !PROT.auto || protDeclined(cart)) return null;
      return { kind: 'add', sig: 'add:' + want[0], want: want, have: have };
    }
    if (!covered) return { kind: 'alone', sig: 'alone', have: have };
    if (have.length === 1 && have[0].quantity === 1 && have[0].variant_id === want[0]) return null;
    return { kind: 'swap', sig: 'swap:' + want[0], want: want, have: have };
  }
  function protAdd(v) { return api('/cart/add.js', { items: [{ id: v[0], quantity: 1 }] }); }
  // By line key, one at a time. Each answer is the whole cart; the last one is what comes back.
  function protRemove(lines) {
    var p = Promise.resolve(null);
    lines.forEach(function (li) {
      p = p.then(function () { return api('/cart/change.js', { id: li.key, quantity: 0 }); });
    });
    return p;
  }
  function protMark(declined) {
    return api('/cart/update.js', { attributes: { sl_prot_declined: declined ? '1' : '' } });
  }
  // Takes a cart, hands back the cart to draw. Never rejects. One correction per pass: what comes back from it is
  // drawn as it is, never settled again in the same pass.
  function protSettle(cart) {
    if (!PROT && !PROT_GONE) return Promise.resolve(cart);
    // On /cart the page's own block (southern.js) may have a correction of its own on the wire at page load, and
    // it reloads the page when that lands. A second one from here would double the line: stand down meanwhile.
    if (window.__slProtBusy) return Promise.resolve(cart);
    if (protRun) {
      // a correction is already in flight (the drawer was opened twice): wait for it, then look at the cart afresh
      return protRun.then(function () { return getCart().then(protSettle); }).catch(function () { return cart; });
    }
    var need = protNeed(cart);
    if (!need || protTried[need.sig]) return Promise.resolve(cart);
    var work;
    if (need.kind === 'alone' || need.kind === 'gone') work = protRemove(need.have);
    else if (need.kind === 'add') work = protAdd(need.want).then(getCart);
    // out first, then in: if the second step fails the shopper is not charged twice, and the row says so
    else work = protRemove(need.have).then(function () { return protAdd(need.want); }).then(getCart);
    protRun = work.then(function (c) {
      var again = protNeed(c);
      if (again && again.sig === need.sig) {   // it did not take: leave it alone until the shopper's next action
        protTried[need.sig] = true;
        if (need.kind === 'swap') protFlash = PROT_ERR;
      }
      return c;
    }, function () {
      protTried[need.sig] = true;
      // A swap that failed leaves the old tier on, or protection off: either way the row is not what this cart
      // should show, so say so. A default add that failed says nothing (the shopper asked for nothing).
      // 9 Oct 2026 (review 2): if the cart cannot be read after that either (the catch on the read below), what
      // is drawn is the cart from BEFORE this pass, and a write of this pass may have landed (the add went in,
      // then the read failed). The row then says the cart could not be updated, whatever the kind, so it never
      // shows "off" over a charge without a word. The next tap or open reads the real cart.
      if (need.kind === 'swap') protFlash = PROT_ERR;
      return getCart().catch(function () { protFlash = PROT_ERR; return cart; });
    }).catch(function () { protFlash = PROT_ERR; return cart; })
      .then(function (c) { protRun = null; return c; });
    return protRun;
  }
  function protSay(t) {
    if (!protMsg) return;
    protMsg.textContent = t || '';
    protMsg.hidden = !t;
  }
  // The row follows the cart: shown while there is something that ships and a tier covers it, switch on while the
  // line is in the cart, and the price is what Shopify is charging for it (on) or what this cart's tier costs (off).
  function protDraw(cart) {
    if (!PROT) return;
    var have = protOf(cart), show = linesTotal(shipsOf(cart)) > 0;
    var want = protWant(cart);
    if (!want) show = false;
    protEl.hidden = !show;
    protToggle.checked = have.length > 0;
    if (protPrice) protPrice.textContent = money(have.length ? linesTotal(have) : (want ? want[2] : 0));
    protSay(show ? protFlash : '');
    protFlash = '';
  }
  if (PROT) {
    protToggle.addEventListener('change', function () {
      var wantOn = !!protToggle.checked;
      protToggle.disabled = true;
      protSay('');
      protFresh();
      busy(true);
      // The attribute first, both ways (as /cart does). Off: the decline has to land before the line comes out,
      // or "add by default" would put it straight back. On: clearing it first means a failed add leaves nothing
      // half done. update.js answers with the whole cart, so the tier and the lines come from that, not from a
      // cart drawn a while ago.
      protMark(!wantOn)
        .then(function (c) {
          var have = protOf(c), want = protWant(c);
          // (no tier covers the cart any more, changed in another tab since the row was drawn: nothing to add)
          if (wantOn) return have.length || !want ? c : protAdd(want).then(getCart);
          return have.length ? protRemove(have) : c;
        })
        .then(protSettle)
        .then(function (c) { render(c); syncCount(c); })
        .catch(function () {
          // Nothing is assumed: draw what the cart really holds (after a failed call, what it held before the
          // tap), which puts the switch back where it was, and say so on the row.
          protFlash = PROT_ERR;
          return getCart()
            .then(function (c) { render(c); syncCount(c); })
            .catch(function () { protFlash = ''; protToggle.checked = !wantOn; protSay(PROT_ERR); });
        })
        .then(function () { protToggle.disabled = false; busy(false); });
    });
  }

  /* ---------- "Complete your setup" rail + pack upgrade ----------
     24 Sep 2026, Josh picked mockup D ("A + B"): a swipe row of the things that finish THIS build, placed above
     the subtotal so it is on screen, and a one-tap pack upgrade on rock light lines (upgradeFor below).
     Second pass the same night (Josh: "if someone has switchback wheel lights recommend them switchback trac bar
     lights + harness, if someone has switchback trac bar lights recommend them the switchback wheel lights, etc,
     make the recommendations make sense"). Every pick now matches the build in STYLE and, for pure white, in
     INTENSITY:
       switchback wheel lights  -> switchback trac bar (Matching), harness, switchback extension wires, rock set
       pure white wheel lights  -> pure white trac bar at the same intensity (Matching), harness, rock set, valance
       switchback trac bar      -> quad row switchback wheel lights (Matching), harness, extension wires, rock set
       pure white trac bar      -> pure white wheel lights at the same intensity (Matching), harness, rock set, valance
       rock lights              -> harness, magnet mounts sized by count, 50 ft wire, pure white valance
       valance                  -> rock light set, harness, pure white trac bar
       strips / brackets only   -> brackets and extension wires in the strip's style (a repair, not a new build)
     Mixed carts take the union, ranked by the numbers below, one card per product, four at most (desktop shows
     the first three). Facts behind it: the harness is sold for rock light and wheel light installs and the
     trac bar and valance pages both say to wire them to "one of our harnesses"; trac bar strips take the wheel
     light extension wires; rock lights and valance only come in pure white. Nothing already in the cart and
     nothing unbuyable is ever shown (the pool only carries available products and variants). Empty cart: no rail.
     No discounts anywhere in here. */
  var HARNESS = 'wiring-harness-wireless-remote';
  var MOUNTS = 'rock-light-magnetic-mounts';
  var WIRE = '50ft-encased-2pin-16ga-wire';
  var EXT = 'wheel-light-extension-wires';
  var STRIPS = 'wheel-light-replacement-strips';
  var BRACKETS = 'wheel-light-replacement-brackets';
  var TRAC_SB = 'switchback-trac-bar-lights-w-startup-animation';
  var TRAC_PW = 'pure-white-trac-bar-lights';
  var WHEEL_SB = 'quad-row-switchback-wheel-lights';
  var WHEEL_PW_STD = 'pure-white-standard-intensity-wheel-lights';
  var WHEEL_PW_HIGH = 'pure-white-high-intensity-wheel-lights';
  var VALANCE = 'plug-play-valance-light-kit';
  var SHORT = {};
  SHORT[HARNESS] = 'Wiring harness + wireless remote';
  SHORT[WIRE] = '50 ft wire for longer runs';
  SHORT[BRACKETS] = 'Stainless wheel light brackets';
  SHORT[TRAC_SB] = 'Switchback trac bar lights';
  SHORT[WHEEL_SB] = 'Quad row switchback wheel lights';
  SHORT[WHEEL_PW_STD] = 'Pure white wheel lights, standard intensity';
  SHORT[WHEEL_PW_HIGH] = 'Pure white wheel lights, high intensity';

  function byHandle(h) {
    for (var i = 0; i < pool.length; i++) { if (pool[i].h === h) return pool[i]; }
    return null;
  }
  function setSize(opt) {
    var m = /Set of (\d+)/i.exec(String(opt || ''));
    return m ? parseInt(m[1], 10) : 0;
  }
  function ledSize(title) {
    var m = /(\d+)\s*LED/i.exec(String(title || ''));
    return m ? m[1] : '';
  }
  function lineType(li) { return String((li && li.product_type) || '').toLowerCase(); }
  function squash(s) { return String(s || '').toLowerCase().replace(/\s+/g, ''); }
  function isSwitchback(li) { return /switchback/i.test(String((li && li.product_title) || '') + ' ' + String((li && li.variant_title) || '')); }
  // the first buyable variant of a pooled product whose option1 passes the test
  function variantOf(p, re) {
    if (!p || !p.vs) return null;
    for (var i = 0; i < p.vs.length; i++) { if (re.test(String(p.vs[i][1] || ''))) return p.vs[i]; }
    return null;
  }

  function recommend(cart) {
    // 9 Oct 2026: shipping protection is not part of the build. The rail never sees the line, so a cart is
    // judged on its products alone.
    var items = ((cart && cart.items) || []).filter(function (li) { return !isProt(li); });
    if (!items.length) return [];
    var inCart = {};
    items.forEach(function (li) { inCart[li.handle] = true; });
    function lines(type) { return items.filter(function (li) { return lineType(li) === type; }); }
    var rock = lines('rock lights'), wheel = lines('wheel lights'), trac = lines('trac bar lights'), val = lines('valance lights');
    var anyLights = rock.length || wheel.length || trac.length || val.length || lines('starlights').length;

    var cands = [];
    // pri: lower shows first. extra overrides the pooled fields (id/p for a specific variant, t for the card name).
    function want(pri, p, extra, evenIfInCart) {
      if (!p || !p.id || (inCart[p.h] && !evenIfInCart)) return;
      cands.push({ pri: pri, p: p, extra: extra || {} });
    }
    function cheapest(type) {
      var best = null;
      pool.forEach(function (p) {
        if (String(p.ty || '').toLowerCase() !== type || inCart[p.h]) return;
        if (!best || p.p < best.p) best = p;
      });
      return best;
    }
    // "9 LED Pure White Rock Lights, set of 4": say which pack the price is for
    function withSet(p) {
      if (p && p.vs && p.vs[0] && p.vs[0][0] === p.id && setSize(p.vs[0][1])) return { t: p.t + ', set of ' + setSize(p.vs[0][1]) };
      return {};
    }
    function rockSet(pri) { var r = cheapest('rock lights'); if (r) want(pri, r, withSet(r)); }
    function valance(pri) {
      var vp = byHandle(VALANCE);
      if (!vp) return;
      var vv = vp.vs && vp.vs[0];
      want(pri, vp, vv ? { id: vv[0], p: vv[3], t: 'Pure white valance lights, ' + String(vv[1] || '').toLowerCase() }
                       : { t: 'Pure white valance lights' });
    }
    function extWires(pri, switchback) {
      var ep = byHandle(EXT);
      var ev = variantOf(ep, switchback ? /switchback/i : /pure white/i);
      if (ev) want(pri, ep, { id: ev[0], p: ev[3], t: 'Wheel light extension wires, ' + (switchback ? 'switchback' : 'pure white') });
    }

    // wheel lights: the trac bar in the same style, and for pure white the same intensity
    if (wheel.length && !trac.length) {
      if (isSwitchback(wheel[0])) {
        want(1, byHandle(TRAC_SB), { t: SHORT[TRAC_SB], badge: 'Matching' });
      } else {
        var high = /high|extreme|ultimate/i.test(String(wheel[0].product_title || ''));
        var tp = byHandle(TRAC_PW), tv = variantOf(tp, high ? /high/i : /standard/i);
        if (tv) want(1, tp, { id: tv[0], p: tv[3], t: 'Pure white trac bar lights, ' + (high ? 'high' : 'standard') + ' intensity', badge: 'Matching' });
      }
    }
    // trac bars: the wheel lights in the same style, and for pure white the same intensity
    if (trac.length && !wheel.length) {
      if (isSwitchback(trac[0])) {
        want(1, byHandle(WHEEL_SB), { t: SHORT[WHEEL_SB], badge: 'Matching' });
      } else {
        var wh = /high/i.test(String((trac[0].variant_options || [])[0] || '') + ' ' + String(trac[0].variant_title || '')) ? WHEEL_PW_HIGH : WHEEL_PW_STD;
        want(1, byHandle(wh), { t: SHORT[wh], badge: 'Matching' });
      }
    }
    // valance: rock lights are the other half of a pure white underglow
    if (val.length && !rock.length) rockSet(1.5);

    // the harness: the most common add-on, sold for rock and wheel light installs, and what the trac bar and
    // valance pages tell people to wire to
    if (anyLights) want(2, byHandle(HARNESS), { t: SHORT[HARNESS], badge: 'Most added' });

    // magnet mounts: count the standard-mount rock lights per LED size (set size x quantity), take off the
    // magnet mounts already in the cart for that LED size, and offer the one mount set that covers exactly
    // what is left. Checked by count, not by handle, so the card comes back after a pack upgrade or a
    // quantity bump. No exact match: no card (never a wrong size). Magnetic-mount lines never count.
    var mp = byHandle(MOUNTS);
    if (mp && mp.vs) {
      var need = {}, ledOrder = [];
      rock.forEach(function (li) {
        var o = li.variant_options || [];
        if (!/standard/i.test(String(o[1] || ''))) return;
        var led = ledSize(li.product_title), c = setSize(o[0]) * (li.quantity || 1);
        if (!led || !c) return;
        if (!(led in need)) { need[led] = 0; ledOrder.push(led); }
        need[led] += c;
      });
      items.forEach(function (li) {
        if (li.handle !== MOUNTS) return;
        var o = li.variant_options || [];
        var mled = squash(o[1]).replace(/led$/, '');
        if (mled in need) need[mled] -= setSize(o[0]) * (li.quantity || 1);
      });
      for (var i = 0; i < ledOrder.length; i++) {
        var owed = need[ledOrder[i]], hit = null;
        if (owed <= 0) continue;
        for (var j = 0; j < mp.vs.length; j++) {
          var v = mp.vs[j];
          if (setSize(v[1]) === owed && squash(v[2]) === ledOrder[i] + 'led') { hit = v; break; }
        }
        if (hit) { want(2.5, mp, { id: hit[0], p: hit[3], t: 'Magnet mounts, ' + (ledOrder.length > 1 ? ledOrder[i] + ' LED, ' : '') + 'set of ' + owed }, true); break; }
      }
    }

    // extension wires in the build's style: wheel lights and trac bar strips both take them
    if (wheel.length || trac.length) extWires(3, wheel.concat(trac).some(isSwitchback));
    // wire for longer runs, with rock lights
    if (rock.length) want(3.5, byHandle(WIRE), { t: SHORT[WIRE] });
    // wheel light and trac bar builds without rock lights: the entry rock light set
    if (!rock.length && (wheel.length || trac.length)) rockSet(4);
    // valance, pure white: with rock lights, and with pure white wheel lights or trac bars
    if (!val.length && (rock.length || wheel.concat(trac).some(function (li) { return !isSwitchback(li); }))) valance(5);
    // valance-only builds: a pure white trac bar
    if (val.length && !rock.length && !wheel.length && !trac.length) {
      var tp2 = byHandle(TRAC_PW), tv2 = variantOf(tp2, /standard/i);
      if (tv2) want(3, tp2, { id: tv2[0], p: tv2[3], t: 'Pure white trac bar lights, standard intensity' });
    }

    if (!anyLights) {
      // a repair (a spare strip, brackets, extension wires): more of the same job, in the strip's style
      var strips = items.filter(function (li) { return li.handle === STRIPS; });
      var repair = strips.length || inCart[BRACKETS] || inCart[EXT];
      // mounts pick their own rock lights below (same LED size and count); never a generic set that may not fit
      var mountsIn = !!inCart[MOUNTS];
      if (strips.length) {
        want(1, byHandle(BRACKETS), { t: SHORT[BRACKETS] });
        extWires(2, strips.some(isSwitchback));
      }
      // extension wires on their own: the harness they get wired to
      if (inCart[EXT] && !strips.length) want(1, byHandle(HARNESS), { t: SHORT[HARNESS] });
      // magnet mounts on their own: the rock lights they fit, same LED size and count, standard mounting
      items.forEach(function (li) {
        if (li.handle !== MOUNTS) return;
        var o = li.variant_options || [], led = squash(o[1]).replace(/led$/, ''), n = setSize(o[0]) * (li.quantity || 1);
        pool.forEach(function (p) {
          if (String(p.ty || '').toLowerCase() !== 'rock lights' || ledSize(p.t) !== led || !p.vs) return;
          p.vs.forEach(function (rv) {
            if (setSize(rv[1]) === n && /standard/i.test(String(rv[2] || ''))) want(1, p, { id: rv[0], p: rv[3], t: p.t + ', set of ' + n });
          });
        });
      });
      // a harness or wire on its own: the lights it is for
      if (inCart[HARNESS] || inCart[WIRE]) { if (!repair && !mountsIn) rockSet(2); want(3, byHandle(WIRE), { t: SHORT[WIRE] }); }
      // anything else (merch, a gift card): the entry rock light set. A repair (brackets, strips) never gets a
      // new light kit pushed at it: repair picks or no rail.
      if (!cands.length && !repair && !mountsIn) rockSet(5);
    }

    cands.sort(function (a, b) { return a.pri - b.pri; });
    var picks = [], seen = {};
    for (var k = 0; k < cands.length && picks.length < 4; k++) {
      var c = cands[k];
      if (seen[c.p.h]) continue;
      seen[c.p.h] = true;
      picks.push(Object.assign({}, c.p, c.extra));
    }
    return picks;
  }

  /* One-tap pack upgrade on a rock light line: the next set size up in the SAME mounting, only when that
     variant is buyable, only on a line of quantity 1 (a quantity of 2 would make "for $X more" ambiguous),
     hidden at the biggest set. Prices are the variants' own list prices, so the "a light" figures are exactly
     the per-light prices the product page shows. */
  function upgradeFor(li) {
    if (!li || lineType(li) !== 'rock lights' || li.quantity !== 1) return null;
    var p = byHandle(li.handle);
    if (!p || !p.vs) return null;
    var o = li.variant_options || [];
    var n = setSize(o[0]);
    var mount = String(o[1] || '');
    if (!n) return null;
    var best = null;
    p.vs.forEach(function (v) {
      var vn = setSize(v[1]);
      if (vn > n && String(v[2] || '') === mount && v[0] !== li.variant_id && (!best || vn < best.n)) {
        best = { id: v[0], n: vn, p: v[3] };
      }
    });
    if (!best) return null;
    // the bigger set is already in the cart (a swap whose removal step failed): never offer to stack another
    if (((lastCart && lastCart.items) || []).some(function (x) { return x.variant_id === best.id; })) return null;
    var now = (CFG.retail && CFG.retail[li.variant_id]) || li.original_price || li.price || 0;
    var add = best.p - now;
    if (add <= 0) return null;
    return { id: best.id, n: best.n, add: add, perNow: Math.round(now / n), perNext: Math.round(best.p / best.n) };
  }

  /* ---------- a refused or cut add (15 Oct 2026) ----------
     Shopify answers 422 when an add, or a + in this drawer, asks for more than it has: "The maximum quantity of this
     item is already in your cart." / "Only 1 item was added to your cart due to availability." (seen on the live
     store, 15 Oct). It can also add fewer than asked. That used to show nothing: the buy form fell back to a full form
     post and the page just reloaded, and + did nothing. Now the drawer draws the real cart and says so by that line,
     or at the top when the option is not in the cart (then in Shopify's own words, e.g. sold out). Any other refusal
     shows Shopify's own message. Nothing here sends a request: the note rides on the next draw, once, like protFlash.
     Josh approved the wording on 15 Oct. */
  var STOCK_MSG = 'That\'s all we have in stock right now.';
  var addFlash = null;   // { k: line key, v: variant id, stock: true|false, msg: Shopify's words }
  function stockRefusal(err) {
    return /availab|maximum quantity|already in your cart|are in your cart|out of stock|in stock|can.?t add more/i
      .test(String((err && err.shopMsg) || ''));
  }
  function flashFor(where, err) {
    // a 400 is our request out of step with the cart (a stale line number), not a refusal: its words are not for shoppers
    var msg = String((err && err.status !== 400 && err.shopMsg) || '');
    // "... is already sold out" names the product, which can be the harness add-on rather than the line: Shopify's
    // own words, at the top
    var gone = /sold out/i.test(msg);
    addFlash = { k: gone ? '' : (where.k || ''), v: gone ? 0 : (where.v || 0), stock: !gone && (!err || stockRefusal(err)), msg: msg };
  }
  function drawFlash(cart) {
    var f = addFlash;
    addFlash = null;
    if (!f) return;
    var items = (cart && cart.items) || [], at = -1, i;
    for (i = 0; i < items.length && at < 0; i++) { if (f.k && items[i].key === f.k) at = i; }
    for (i = 0; i < items.length && at < 0; i++) { if (f.v && items[i].variant_id === f.v) at = i; }
    var row = at > -1 ? itemsEl.querySelector('[data-line="' + (at + 1) + '"]') : null;
    var t = f.stock && row ? STOCK_MSG : (f.msg || (f.stock ? STOCK_MSG : PROT_ERR));
    // the same note, class and place as the lead-time cap below (an li of its own when there is no row for it)
    var m = document.createElement(row ? 'p' : 'li');
    m.className = 'sl-cd__qmsg';
    m.setAttribute('data-cd-qmsg', '');
    m.setAttribute('role', 'status');
    m.textContent = t;
    var qb = row && row.querySelector('.sl-cd__qty');
    if (row) (qb && qb.parentNode ? qb.parentNode : row).appendChild(m);
    else itemsEl.insertBefore(m, itemsEl.firstChild);
  }

  /* ---------- render ---------- */
  /* Dealer accounts (29 Sep 2026): struck regular retail after the line price. CFG.dealerWas only exists for dealers. */
  function dealerWas(li) {
    var w = CFG.dealerWas && CFG.dealerWas[li.variant_id];
    return w && w * li.quantity > li.final_line_price ? ' <s class="sl-cd__was">' + esc(money(w * li.quantity)) + '</s>' : '';
  }

  /* Dealer retail view (30 Sep 2026): the dealer switched "Dealer prices" off, so lines and the subtotal show
     retail. CFG.retail only exists then; otherwise these return Shopify's own numbers. */
  function linePrice(li) {
    var r = CFG.retail && CFG.retail[li.variant_id];
    return r ? Math.round(r * li.quantity) : li.final_line_price;
  }
  function cartTotal(cart) {
    if (!CFG.retail) return cart.total_price;
    return (cart.items || []).reduce(function (s, li) { return s + linePrice(li); }, 0);
  }

  function renderShip(total) {
    if (window.SL_DEALER) {  /* dealer accounts pay UPS Ground (29 Sep 2026) */
      shipMsg.textContent = 'Dealer order: UPS Ground shipping is calculated at checkout.';
      barFill.style.transform = 'scaleX(1)';
      return;
    }
    var left = THRESH - total;
    if (left <= 0) {
      shipMsg.innerHTML = '<span class="ok">Your US order ships free.</span>';
      barFill.style.transform = 'scaleX(1)';
    } else {
      shipMsg.innerHTML = '<span class="ok">Your US order ships free.</span>';
      barFill.style.transform = 'scaleX(1)';
    }
  }

  function render(cart) {
    var n = countOf(cart);   // products only: protection is the switch row, not an item (9 Oct 2026)
    emptyEl.hidden = n > 0;
    subWrap.hidden = n === 0;
    var _r = document.querySelector('[data-sl-cart-drawer]');
    if (_r) _r.classList.toggle('sl-cd--empty', n === 0);
    lastCart = cart;
    var stands = protStands(cart);
    itemsEl.innerHTML = (cart.items || []).map(function (li, i) {
      // 9 Oct 2026: the protection line is the row with the switch, never a product row. The index still counts
      // it, so data-line on the rows that are drawn stays Shopify's line number. (Review 2: only while the row
      // stands in for it. A line the row cannot be shown for is drawn like any other, see protStands.)
      if (stands && isProt(li)) return '';
      var img = li.image ? li.image.replace(/(\.[a-z]+)(\?|$)/i, '_120x$1$2') : '';
      var vt = (li.variant_title && li.variant_title !== 'Default Title')
        ? '<div class="sl-cd__vt">' + esc(li.variant_title) + '</div>' : '';
      var ns = isSale(li)
        ? '<span class="sl-cd__nostack">Already reduced deeper than any code &middot; codes don&rsquo;t apply</span>' : '';
      var up = upgradeFor(li);
      var upHtml = up
        ? '<div class="sl-cd__up"><div class="t"><span class="nw">Upgrade to <b>' + up.n + ' lights</b></span> <span class="nw">for ' + esc(money(up.add)) + ' more</span>' +
          (up.perNext < up.perNow
            ? '<small>' + esc(money(up.perNext)) + ' a light instead of ' + esc(money(up.perNow)) + '.</small>' : '') +
          '</div><button class="sl-cd__upgo" type="button" data-cd-upgrade="' + esc(li.key) + '" data-to="' + up.id + '">Upgrade</button></div>'
        : '';
      return '<li class="sl-cd__row" data-line="' + (i + 1) + '">' +
        (img ? '<img src="' + esc(img) + '" alt="" loading="lazy">' : '<span></span>') +
        '<div><div class="sl-cd__nm">' + esc(li.product_title) + '</div>' + vt +
        '<div class="sl-cd__pr">' + esc(money(linePrice(li))) + dealerWas(li) + '</div>' + ns +
        '<div class="sl-cd__qty">' +
          '<button type="button" data-cd-dec aria-label="Reduce quantity">&minus;</button>' +
          '<span>' + li.quantity + '</span>' +
          '<button type="button" data-cd-inc aria-label="Increase quantity">+</button>' +
        '</div></div>' +
        '<button class="sl-cd__rm" type="button" data-cd-rm aria-label="Remove">&times;</button>' +
        upHtml +
        '</li>';
    }).join('');
    try { drawFlash(cart); } catch (e) {}   // 15 Oct 2026: a refused or cut add says so here, once (see above)

    subEl.textContent = money(cartTotal(cart));
    var footSub = root.querySelector('[data-cd-footsub]');
    if (footSub) footSub.textContent = money(cartTotal(cart));
    protDraw(cart);
    renderShip(cart.total_price || 0);
    /* 1 Oct 2026: the drawer's shipping line (cartCutoff in sl-cart-drawer.liquid) follows lead times. */
    try { document.dispatchEvent(new CustomEvent('sl:cart', { detail: cart })); } catch (e) {}

    /* Cart-level version of the same fact. The per-line badge says WHICH lines
       are affected; this says what happens when they enter a code, which is the
       question they are actually about to have. Wording mirrors the cart page so
       the two surfaces cannot drift apart. No user data is interpolated, so the
       entities are safe to set as markup. */
    if (nostackEl) {
      // The lines this drawer draws: with the protection row on the page, the products only (9 Oct 2026).
      // sections/main-cart.liquid counts the same way (it does not print the protection line either).
      var items = stands ? goodsOf(cart) : (cart.items || []);
      var marked = items.filter(isSale).length;
      nostackEl.hidden = marked === 0;
      if (marked) {
        // Mirrors sections/main-cart.liquid word-for-word — change together.
        // "Deeper than any code" holds while every sl-on-sale markdown
        // (currently all 25%) beats the deepest live code (20%).
        nostackEl.innerHTML = marked === items.length
          ? 'Everything in your cart is already marked down, a deeper ' +
            'cut than any code we send, so a code won&rsquo;t apply on top. ' +
            'You&rsquo;re not missing a better deal: this price beats them.'
          : marked + ' item' + (marked > 1 ? 's' : '') + ' above ' +
            (marked > 1 ? 'are' : 'is') + ' already marked down, so a discount ' +
            'code won&rsquo;t come off ' + (marked > 1 ? 'those' : 'that') + '. ' +
            'It will still apply to the rest of your cart.';
      }
    }

    var recs = recommend(cart);
    recsWrap.hidden = !recs.length;
    recList.innerHTML = recs.map(function (p) {
      return '<li class="sl-cd__card">' +
        (p.badge ? '<span class="sl-cd__badge">' + esc(p.badge) + '</span>' : '') +
        (p.img ? '<img src="' + esc(p.img) + '" alt="" loading="lazy" width="240" height="240">' : '') +
        '<div class="nm">' + esc(p.t) + '</div>' +
        '<div class="pr">' + esc(money(p.p)) + '</div>' +
        '<button class="sl-cd__add" type="button" data-cd-recadd="' + p.id + '" aria-label="' + esc('Add ' + p.t + ', ' + money(p.p)) + '">+ Add</button>' +
        '</li>';
    }).join('');
  }

  function refresh() {
    return getCart().then(protSettle).then(function (c) { render(c); syncCount(c); return c; });
  }

  function syncCount(cart) {
    var n = countOf(cart);
    var badge = document.querySelector('.sl-cart-count');
    if (!badge && n) {
      var cartBtn = document.querySelector('.sl-iconbtn--cart');
      if (cartBtn) {
        badge = document.createElement('span');
        badge.className = 'sl-cart-count';
        cartBtn.appendChild(badge);
      }
    }
    if (badge) {
      badge.textContent = n;
      badge.style.display = n ? '' : 'none';
    }
  }

  /* ---------- open / close ---------- */
  var lastFocus = null;

  function open() {
    lastFocus = document.activeElement;
    root.hidden = false;
    // âš ï¸ Reading offsetWidth, not requestAnimationFrame. rAF does not fire in a
    // background or unfocused tab, so the panel stayed at translateX(100%) and
    // the drawer looked like it had simply failed to open. Forcing a reflow
    // flushes the style so the transition still animates, and it is synchronous.
    void root.offsetWidth;
    root.classList.add('is-open');
    document.documentElement.style.overflow = 'hidden';
    leaving = false;
    protFresh();
    busy(true);
    refresh().then(function () { busy(false); })
      .catch(function () { busy(false); });
    var x = $('.sl-cd__x');
    if (x) x.focus();
  }

  function close() {
    root.classList.remove('is-open');
    document.documentElement.style.overflow = '';
    setTimeout(function () { root.hidden = true; }, 280);
    if (lastFocus && lastFocus.focus) lastFocus.focus();
    // 9 Oct 2026: on /cart, a change made in the drawer left the page under it stale (see ON_CART_PAGE above).
    if (ON_CART_PAGE && cartWrote) {
      if (busyEl.hidden) window.location.reload(); else leaving = true;
    }
  }

  root.addEventListener('click', function (e) {
    if (e.target.closest('[data-cd-close]')) { close(); return; }
  });
  document.addEventListener('keydown', function (e) {
    // 9 Oct 2026: with "What's covered" open on the protection row, Escape closes that first and the drawer on
    // the next press. southern.js handles it ahead of this listener and stops the event there; defaultPrevented
    // is the second lock, in case the event still gets here.
    if (e.key === 'Escape' && !root.hidden && !e.defaultPrevented) close();
  });

  // Keep focus inside the panel while it is open.
  document.addEventListener('focusin', function (e) {
    if (root.hidden || !root.classList.contains('is-open')) return;
    if (!panel.contains(e.target)) {
      var all = panel.querySelectorAll('button:not([disabled]), a[href], input:not([disabled])'), f = [];
      for (var i = 0; i < all.length; i++) { if (all[i].offsetParent !== null) f.push(all[i]); }
      if (!f.length) return;
      // Shift+Tab off the first control wraps to the last one instead of snapping back to the X.
      var goLast = e.relatedTarget && e.relatedTarget === f[0];
      (goLast ? f[f.length - 1] : f[0]).focus();
    }
  });

  /* ---------- pack upgrade ----------
     Add the bigger set FIRST, then take the old line out by its key (retried once, never re-adding). If the add
     fails nothing has been removed. If both removals fail the refresh shows both lines, and upgradeFor hides the
     button because the bigger set is already in the cart, so a second tap cannot stack more lights. Line
     properties ride along, except the lead-time acknowledgement (1 Oct 2026, see upgradeLine). */
  var lastCart = null;
  function upgradeLine(btn) {
    var key = btn.getAttribute('data-cd-upgrade');
    var to = parseInt(btn.getAttribute('data-to'), 10);
    var li = null;
    ((lastCart && lastCart.items) || []).forEach(function (x) { if (x.key === key) li = x; });
    if (!li || !to) return;
    var item = { id: to, quantity: li.quantity || 1 };
    // 1 Oct 2026: every property rides along EXCEPT the lead-time acknowledgement. The bigger set offered here is
    // always "In stock, ready to ship." (the drawer's pool holds in-stock options only), so a Delivery note copied
    // from the old line would be false on it.
    var src = li.properties || {}, props = {};
    if (src && typeof src === 'object') Object.keys(src).forEach(function (k) { if (k !== 'Delivery') props[k] = src[k]; });
    if (Object.keys(props).length) item.properties = props;
    btn.disabled = true;
    protFresh();
    busy(true);
    api('/cart/add.js', { items: [item] })
      .then(function () {
        var rm = function () { return api('/cart/change.js', { id: key, quantity: 0 }); };
        return rm().catch(rm);
      })
      .then(protSettle)   // a bigger pack can cross a protection tier
      .then(function (c) { render(c); syncCount(c); })
      .catch(function () { return refresh().catch(function () {}); })
      .then(function () { busy(false); });
  }

  /* ---------- line item controls ---------- */
  itemsEl.addEventListener('click', function (e) {
    var row = e.target.closest('[data-line]');
    if (!row) return;
    var upBtn = e.target.closest('[data-cd-upgrade]');
    if (upBtn) { upgradeLine(upBtn); return; }
    var line = parseInt(row.getAttribute('data-line'), 10);
    var qtyEl = row.querySelector('.sl-cd__qty span');
    var q = parseInt(qtyEl ? qtyEl.textContent : '1', 10) || 1;
    var next = null;
    if (e.target.closest('[data-cd-inc]')) next = q + 1;
    else if (e.target.closest('[data-cd-dec]')) next = q - 1;
    else if (e.target.closest('[data-cd-rm]')) next = 0;
    if (next === null) return;
    // 1 Oct 2026: an in-stock option with a waiting notice ("Inventory is on the way, ...") sells past its count only
    // with the ship-time checkbox on the product page. + stops at the count on a line that has not acknowledged it,
    // and says why. SL_CART_CFG.leads holds the notice (t), the state (s) and the count (inv).
    if (next > q) {
      var cl = ((lastCart && lastCart.items) || [])[line - 1];
      var lm = cl && CFG.leads && CFG.leads[cl.variant_id];
      if (lm && lm.t && lm.s !== 'way' && typeof lm.inv === 'number' && next > lm.inv && !(cl.properties && cl.properties.Delivery)) {
        var qm = row.querySelector('[data-cd-qmsg]');
        if (!qm) {
          qm = document.createElement('p');
          qm.className = 'sl-cd__qmsg';
          qm.setAttribute('data-cd-qmsg', '');
          qm.setAttribute('role', 'status');
          var qb = row.querySelector('.sl-cd__qty');
          if (qb && qb.parentNode) qb.parentNode.appendChild(qm); else row.appendChild(qm);
        }
        qm.textContent = 'Only ' + lm.inv + ' in stock. More than that ships later: open the product page to add more.';
        return;
      }
    }
    protFresh();
    busy(true);
    // 15 Oct 2026: the line a + raises, so a refused or capped + can say so by it (drawFlash)
    var was = next > q ? ((lastCart && lastCart.items) || [])[line - 1] : null;
    api('/cart/change.js', { line: line, quantity: Math.max(0, next) })
      .then(function (c) {
        // capped with no refusal (fewer than asked, still a 200): the same note
        if (was) ((c && c.items) || []).forEach(function (x) {
          if (x.key === was.key && x.quantity < next) flashFor({ k: was.key, v: was.variant_id }, null);
        });
        return c;
      })
      .then(protSettle)   // the last product gone, or a tier edge crossed: protection follows the cart
      .then(function (c) { render(c); syncCount(c); })
      .catch(function (err) {
        // 15 Oct 2026: Shopify refused or cut the +: draw the real cart and say why by that line. Nothing is resent.
        if (was && err && err.status) {
          flashFor({ k: was.key, v: was.variant_id }, err);
          return refresh().catch(function () { addFlash = null; });
        }
      })
      .then(function () { busy(false); });
  });

  /* 16 Oct 2026 (Clarity, Fri 16 Oct: 3 of the 4 drawer dead-tap sessions were taps on a card's picture or name, and
     only "+ Add" did anything). A tap anywhere on a "Complete your setup" card now adds it, through this same handler
     as its "+ Add" button: same request, same rail rules (recommend() never offers mounts to a magnetic set, the pool
     holds only in-stock options), same disabled button and busy state, then the redraw. The button stays the card's
     one control for keyboards and screen readers; its name now says what it adds. One add at a time: a second tap
     while one is on the wire (a fast double tap) does nothing. A refused add says why by the line or at the top,
     the same note as the buy form ("a refused or cut add" above); nothing is resent. */
  var recAdding = false;
  recList.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-cd-recadd]');
    if (!btn) {
      var card = e.target.closest('.sl-cd__card');
      btn = card ? card.querySelector('[data-cd-recadd]') : null;
    }
    if (!btn || btn.disabled || recAdding) return;
    var id = parseInt(btn.getAttribute('data-cd-recadd'), 10);
    if (!id) return;
    recAdding = true;
    btn.disabled = true;
    protFresh();
    busy(true);
    api('/cart/add.js', { items: [{ id: id, quantity: 1 }] })
      .then(refresh)
      .catch(function (err) {
        btn.disabled = false;
        if (err && err.status) {
          flashFor({ v: id }, err);
          return refresh().catch(function () { addFlash = null; });
        }
      })
      .then(function () { recAdding = false; busy(false); });
  });

  /* ---------- the product form ----------
     One handler, doing what three used to. Order matters: native validation
     first (the built-to-order acknowledgement is a required checkbox and must
     still block), then the line items, then the add. Tracking comes from the
     app pixels, not from here. */
  var form = document.getElementById('SlBuyForm');
  if (form) {
    form.addEventListener('submit', function (e) {
      if (typeof form.checkValidity === 'function' && !form.checkValidity()) return;
      var main = form.querySelector('[name="id"]');
      if (!main || !main.value) return;          // let the browser do its thing
      e.preventDefault();

      var qtyEl = form.querySelector('[name="quantity"]');
      var qty = qtyEl ? (parseInt(qtyEl.value, 10) || 1) : 1;
      var item = { id: parseInt(main.value, 10), quantity: qty };

      // THE LEAD-TIME ACKNOWLEDGEMENT HAS TO RIDE ALONG. Without it the order
      // carries no record that the wait was agreed to, which is the whole point
      // of the checkbox.
      var ack = form.querySelector('[data-sl-ack]');
      // 'Delivery', matching the Liquid forms and southern.js — the property
      // shows in the checkout summary, so the key is customer-facing copy.
      if (ack && ack.checked && ack.value) item.properties = { 'Delivery': ack.value };

      var items = [item];
      // The wiring-harness add-on, if the customer left it ticked. ONE request,
      // not two: two calls can half-succeed and leave them with a harness and
      // no lights. southern.js's harnessAddon handler stands down when this
      // file is present - see the guard there.
      var _boxes = document.querySelectorAll('[data-sl-addon]');
      for (var _bi = 0; _bi < _boxes.length; _bi++) {
        var _b = _boxes[_bi];
        var _t = _b.querySelector('[data-sl-addon-toggle]');
        var _id = parseInt(_b.getAttribute('data-variant'), 10);
        if (_t && _t.checked && _id) items.push({ id: _id, quantity: 1 });
      }

      var btn = form.querySelector('button[type=submit]');
      var label = btn ? btn.textContent : '';
      if (btn) { btn.disabled = true; btn.classList.add('is-loading'); }

      // 9 Oct 2026: the "which button" tag rides in THIS request as a cart attribute. It used to be a second
      // request sent in the same instant (southern.js, stampSrc): with no cart yet Shopify made two carts and
      // about one first add in ten was lost. southern.js now leaves the tag in this form as a hidden field.
      var addBody = { items: items };
      var srcEl = form.querySelector('input[name="attributes[_sl_src]"]');
      if (srcEl && srcEl.value) addBody.attributes = { '_sl_src': srcEl.value };

      api('/cart/add.js', addBody)
        .then(function (d) {
          if (btn) { btn.disabled = false; btn.classList.remove('is-loading'); }
          // 15 Oct 2026: fewer added than asked, with no refusal: say so by the line (drawFlash)
          ((d && d.items) || []).forEach(function (x) {
            if (x.variant_id === item.id && x.quantity < qty) flashFor({ v: item.id }, null);
          });
          open();
        })
        .catch(function (err) {
          if (btn) { btn.disabled = false; btn.classList.remove('is-loading'); }
          // 15 Oct 2026: Shopify answered and refused (or cut) the add, most often because every one left is already
          // in the cart. Never post the form again: that only reloaded the page with no word. Open the drawer on the
          // real cart and say why (drawFlash). Nothing is resent.
          if (err && err.status) {
            flashFor({ v: item.id }, err);
            open();
            return;
          }
          // No answer from Shopify at all (network): fall back to the old behaviour rather than swallow the click.
          form.dataset.slCartFallback = '1';
          form.submit();
        });
    });
  }

  /* Cart icon opens the drawer instead of navigating. The href stays a real
     link so it still works without script and for open-in-new-tab. */
  var icon = document.querySelector('.sl-iconbtn--cart');
  if (icon) {
    icon.addEventListener('click', function (e) {
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
      e.preventDefault();
      open();
    });
  }

  window.SLCart = { open: open, close: close, refresh: refresh, recommend: recommend, upgradeFor: upgradeFor };
})();


/* Order note (3 Nov 2026, Josh: "is there a way for a customer to leave a note at checkout? If so please add it").
 * The box is printed in the drawer (snippets/sl-cart-drawer.liquid) and on /cart (sections/main-cart.liquid), both
 * name="note", the cart note Shopify carries to the order. Each belongs to its own Checkout form, so the note always
 * posts WITH Checkout. This also saves it to the cart as they type, for what posts no form: the express wallets, and
 * the next page the drawer is printed on. Its own block, outside the drawer's above, and it never touches the buy form.
 *   - One save at a time, and never while the drawer has a cart write on the wire (its busy layer is up) or the buy
 *     button is adding: the 9 Oct 2026 rule, no second cart write alongside an add. It waits, then goes.
 *   - keepalive, so a save that starts as they leave (the stepper reloads /cart) still lands.
 *   - The two boxes mirror each other, and a drawn cart (sl:cart) refreshes a box nobody is editing.
 *   - A failed save is not retried in a loop: Checkout still posts the note, and the next keystroke tries again.
 */
(function () {
  'use strict';
  var boxes = Array.prototype.slice.call(document.querySelectorAll('[data-sl-note]'));
  if (!boxes.length || !window.fetch) return;
  var held = boxes[0].value;   // what the cart holds: the value printed with the page
  var want = held, failed = null, timer = null, saving = false, waits = 0;

  function say(t) {
    var els = document.querySelectorAll('[data-sl-note-saved]');
    for (var i = 0; i < els.length; i++) els[i].textContent = t;
  }
  function onTheWire() {
    var b = document.querySelector('[data-cd-busy]');
    return !!((b && !b.hidden) || document.querySelector('#SlBuyForm .is-loading'));
  }
  function later(ms) {
    if (timer) clearTimeout(timer);
    timer = setTimeout(save, ms);
  }
  function save() {
    timer = null;
    if (want === held || want === failed) return;
    if (saving) { later(400); return; }
    if (onTheWire() && waits < 20) { waits++; later(400); return; }   // waits about 8 seconds at most
    waits = 0;
    saving = true;
    var v = want;
    fetch('/cart/update.js', {
      method: 'POST',
      keepalive: true,
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ note: v })
    }).then(function (r) {
      if (!r.ok) throw new Error('note ' + r.status);
      held = v;
      if (want === v) say('Saved');
    }).catch(function () {
      failed = v;
    }).then(function () {
      saving = false;
      if (want !== held && want !== failed) later(300);
    });
  }
  function edit(box, now) {
    if (box.value !== want) {
      want = box.value;
      failed = null;
      for (var i = 0; i < boxes.length; i++) if (boxes[i] !== box) boxes[i].value = want;
      say('');
    }
    later(now ? 0 : 700);
  }
  boxes.forEach(function (box) {
    box.addEventListener('input', function () { edit(box, false); });
    box.addEventListener('change', function () { edit(box, true); });
  });
  window.addEventListener('pagehide', function () { if (want !== held && !saving) save(); });
  document.addEventListener('sl:cart', function (e) {
    var c = e && e.detail;
    if (!c || saving || want !== held) return;   // their edit, not saved yet, wins over the cart that was drawn
    var v = c.note == null ? '' : String(c.note);
    if (v === held) return;
    held = want = v;
    for (var i = 0; i < boxes.length; i++) if (boxes[i] !== document.activeElement) boxes[i].value = v;
    if (v) {
      var w = document.querySelectorAll('[data-sl-note-wrap]');
      for (var j = 0; j < w.length; j++) w[j].open = true;
    }
  });
})();
